import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Lock,
  MagnifyingGlass,
  Funnel,
  Robot,
  ChartBar,
  Lightbulb,
  Code,
  Brain,
  Stack,
  ArrowRight,
  ArrowDown,
  Sparkle,
  Calendar,
  Compass,
  Megaphone,
  Briefcase,
  Presentation,
  GraduationCap,
  X,
  CheckCircle,
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import Navbar from "@/components/Navbar";

const CATEGORY_ICONS = {
  "Strategy & Decisions": Compass,
  "Market Analysis": ChartBar,
  "Personal Branding": Megaphone,
  Presentations: Presentation,
  Professional: Briefcase,
  Legal: Stack,
  Wellness: Lightbulb,
  Finance: ChartBar,
  Technology: Code,
  Education: Brain,
  Default: Robot,
};

const todayLabel = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

export default function GPTGallery() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [gpts, setGpts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCat, setActiveCat] = useState("Action Program");
  const [query, setQuery] = useState("");

  // T&C modal — derived state: open whenever a non-admin user is on the
  // Mentorship tab without having accepted T&C, unless they manually
  // dismissed it. Re-clicking a locked card resets `dismissed` to false.
  const [tncDismissed, setTncDismissed] = useState(false);
  // Orientation-lock modal — opened when user clicks any 6-Months-Mentorship
  // card while T&C is accepted but orientation form isn't submitted yet.
  const [orientationLockGpt, setOrientationLockGpt] = useState(null);

  const loadGpts = async () => {
    try {
      const { data } = await api.get("/gpts");
      setGpts(data);
    } catch (e) {
      setError("Failed to load GPTs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGpts();
    // Long-lived sessions: an admin may have enrolled this participant since the
    // app was opened — refetch the profile so the T&C flow appears without a re-login.
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tabs in fixed order — public Action Program first, locked 6 Months Mentorship second.
  // We only list tabs that actually have at least one GPT visible to this user
  // (non-admins won't see 6 Months Mentorship because the backend hides locked GPTs).
  const categories = useMemo(() => {
    const present = new Set(gpts.map((g) => g.category));
    const ordered = ["Action Program", "6 Months Mentorship"];
    return ordered.filter((c) => present.has(c));
  }, [gpts]);

  // If the default tab isn't present for this user (e.g. nothing seeded in
  // Action Program), fall back to the first available tab. Compute the
  // effective active category synchronously to avoid a render→setState→render
  // ping (which trips react-hooks/set-state-in-effect).
  const effectiveActiveCat =
    categories.length && !categories.includes(activeCat) ? categories[0] : activeCat;

  const filtered = useMemo(() => {
    return gpts.filter((g) => {
      const matchCat = g.category === effectiveActiveCat;
      const q = query.trim().toLowerCase();
      const matchQuery =
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [gpts, activeCat, query]);

  const unlockedCount = gpts.filter((g) => !g.locked).length;
  const totalCount = gpts.length;

  // Mentorship onboarding state, derived from the user object refreshed via
  // AuthContext. Admins and FULL_ACCESS users always satisfy this.
  const isMentorshipUser = !!user && user.role !== "admin";
  // Admin-managed enrollment flag. Non-approved users never see the T&C or
  // orientation flow — they get a "Not enrolled" ribbon instead.
  const mentorshipApproved = user?.role === "admin" || !!user?.mentorship_approved;
  const tncAccepted = user?.role === "admin" || !!user?.mentorship_tnc_accepted;
  const orientationDone =
    user?.role === "admin" || !!user?.mentorship_orientation_submitted;
  const onboardingDone = tncAccepted && orientationDone;
  const mentorshipTabActive = effectiveActiveCat === "6 Months Mentorship";

  // T&C modal: only auto-opens for APPROVED non-admin users who haven't
  // accepted yet. Non-approved users see the "Not enrolled" ribbon instead.
  const tncModalOpen =
    !loading &&
    mentorshipTabActive &&
    isMentorshipUser &&
    mentorshipApproved &&
    !tncAccepted &&
    !tncDismissed;

  // Switching to the Mentorship tab resets the dismissed flag so the modal
  // auto-shows again. Locked-card clicks call this too.
  const reopenTncModal = () => setTncDismissed(false);
  const goToCategory = (cat) => {
    setActiveCat(cat);
    if (cat === "6 Months Mentorship") {
      setTncDismissed(false);
      // Pull the latest enrollment/unlock state the moment the tab is opened.
      refresh().then((fresh) => { if (fresh) loadGpts(); });
    }
  };

  // After T&C accepted but orientation pending, surface a subtle ribbon.
  const showOrientationRibbon =
    mentorshipTabActive &&
    isMentorshipUser &&
    mentorshipApproved &&
    tncAccepted &&
    !orientationDone;

  // Not-enrolled ribbon — for users the admin hasn't approved yet. They see
  // the tab + locked cards but NO T&C / orientation flow.
  const showNotEnrolledRibbon =
    mentorshipTabActive && isMentorshipUser && !mentorshipApproved;

  return (
    <div className="gallery-shell" data-testid="gpt-gallery-page">
      <Navbar />

      <main className="gallery-container">
        {/* Hero — clean Dashboard headline */}
        <section className="hero-row" data-testid="hero-section">
          <div className="hero-title-block">
            <div className="hero-display">Dashboard</div>
          </div>
          <div className="hero-controls">
            <span className="pill-soft" data-testid="hero-date-pill">
              <Calendar size={16} weight="duotone" className="pill-icon" />
              {todayLabel()}
            </span>
            <Link to={user?.role === "admin" ? "/admin" : "#"} className="pill-cta" data-testid="hero-primary-cta">
              {user?.role === "admin" ? "Admin" : "Member"}
              <span className="pill-cta-arrow"><ArrowRight size={14} weight="bold" /></span>
            </Link>
          </div>
        </section>

        {/* Bento stats */}
        <section className="bento-grid" data-testid="bento-stats">
          {/* Wellness-style: Active Agents */}
          <article className="bento bento-col-4 bento-feature">
            <div className="bento-head">
              <span className="bento-tag">
                <span className="bento-tag-dot" /> Active
              </span>
              <div className="bento-mini-icon"><Sparkle size={16} weight="duotone" /></div>
            </div>
            <div className="bento-foot">
              <div className="bento-value">
                {unlockedCount}
                <span className="bento-value-sub">↑ {Math.round((unlockedCount / Math.max(totalCount, 1)) * 100)}%</span>
              </div>
              <div className="bento-label">Available agents</div>
            </div>
          </article>

          {/* Lime stat: total agents */}
          <article className="bento bento-col-4 b-lime">
            <div className="bento-head">
              <span className="bento-tag">
                <Sparkle size={10} weight="fill" /> Library
              </span>
              <div className="bento-mini-icon"><Robot size={16} weight="duotone" /></div>
            </div>
            <div className="bento-foot">
              <div className="bento-value">{totalCount}</div>
              <div className="bento-label">Total agents in hub</div>
            </div>
          </article>

          {/* Greeting bento */}
          <article className="bento bento-col-4 b-ink">
            <div className="bento-head">
              <span className="bento-tag">
                <span className="bento-tag-dot" /> Plan
              </span>
              <div className="bento-mini-icon"><ArrowDown size={16} weight="bold" /></div>
            </div>
            <div className="bento-foot">
              <div className="bento-value" style={{ fontSize: "1.75rem", textTransform: "capitalize" }}>
                {user?.role === "admin" ? "Admin" : "Member"}
              </div>
              <div className="bento-label">Welcome, {user?.name?.split(" ")[0] || "there"}</div>
            </div>
          </article>
        </section>

        {/* Section strip */}
        <div className="section-strip">
          <div>
            <p className="section-strip-meta">// Pick an expert</p>
            <h2 className="section-strip-title">
              Choose your <em>specialist</em>
            </h2>
          </div>
          <p className="section-strip-meta">{filtered.length} agent{filtered.length === 1 ? "" : "s"}</p>
        </div>

        {/* Filter bar (single pill row) */}
        <section className="filter-bar" data-testid="filter-bar">
          <div className="gallery-search">
            <MagnifyingGlass size={16} weight="bold" className="gallery-search-icon" />
            <input
              type="text"
              placeholder="Search agents..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="gallery-search-input"
            />
          </div>
          <div className="filter-divider" />
          <Funnel size={16} weight="duotone" style={{ color: "var(--muted)" }} />
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => goToCategory(cat)}
              className={`chip-pill ${effectiveActiveCat === cat ? "is-on" : ""}`}
              data-testid={`filter-chip-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </section>

        {/* Not-enrolled ribbon — admin hasn't approved this user yet. They
            see the tab + locked cards but NO T&C / orientation flow. */}
        {showNotEnrolledRibbon && (
          <div className="mentorship-ribbon mentorship-ribbon-locked" data-testid="mentorship-not-enrolled-ribbon">
            <span className="mentorship-ribbon-dot mentorship-ribbon-dot-locked" />
            <span className="mentorship-ribbon-text">
              <strong>You&rsquo;re not enrolled in the 6 Months Mentorship yet.</strong>{" "}
              These GPTs are reserved for participants. Contact the admin to enroll.
            </span>
          </div>
        )}

        {/* Subtle inline ribbon ONLY when T&C is done but orientation isn't.
            The big T&C modal handles the "not started" case. */}
        {showOrientationRibbon && (
          <div className="mentorship-ribbon" data-testid="mentorship-orientation-ribbon">
            <span className="mentorship-ribbon-dot" />
            <span className="mentorship-ribbon-text">
              <strong>One step left.</strong> Fill the orientation form to unlock your mentorship GPTs.
            </span>
            <Link
              to="/mentorship/onboarding?step=orientation"
              className="mentorship-ribbon-cta"
              data-testid="mentorship-orientation-cta"
            >
              Continue orientation <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        )}

        {/* Cards */}
        {loading ? (
          <div className="py-20 flex justify-center"><div className="spinner" /></div>
        ) : error ? (
          <div className="text-red-500" data-testid="gallery-error">{error}</div>
        ) : (
          <section className="agent-grid">
            {filtered.map((g, idx) => (
              <AgentCard
                key={g.id}
                gpt={g}
                index={idx}
                requiresOnboarding={
                  g.category === "6 Months Mentorship" &&
                  isMentorshipUser &&
                  mentorshipApproved &&
                  !onboardingDone
                }
                onTncRequired={reopenTncModal}
                onOrientationRequired={() => setOrientationLockGpt(g)}
                tncAccepted={tncAccepted}
              />
            ))}
            {filtered.length === 0 && (
              <div className="empty-grid" data-testid="gallery-empty">
                No agents match your search.
              </div>
            )}
          </section>
        )}
      </main>

      {/* Centered T&C modal — first-touch consent surface for the mentorship
          tier. After "Agree" the user is auto-routed to the orientation form. */}
      {tncModalOpen && (
        <TermsModal
          onClose={() => setTncDismissed(true)}
          onAccepted={(payload) => {
            // Optimistically update user so the modal stops auto-reopening,
            // then navigate to the orientation form.
            if (user) {
              setUser({
                ...user,
                mentorship_tnc_accepted: true,
                mentorship_orientation_submitted: !!payload?.orientation_submitted,
              });
            }
            navigate("/mentorship/onboarding?step=orientation");
          }}
        />
      )}

      {/* Orientation-lock modal — surfaces when a user clicks any 6 Months
          Mentorship card after accepting T&C but before submitting the
          orientation form. Replaces the silent redirect with a clear gate. */}
      {orientationLockGpt && (
        <OrientationLockModal
          gpt={orientationLockGpt}
          onClose={() => setOrientationLockGpt(null)}
          onContinue={() => {
            setOrientationLockGpt(null);
            navigate("/mentorship/onboarding?step=orientation");
          }}
        />
      )}
    </div>
  );
}

function AgentCard({ gpt, index, requiresOnboarding = false, onTncRequired, onOrientationRequired, tncAccepted = false }) {
  const Icon = CATEGORY_ICONS[gpt.category] || CATEGORY_ICONS.Default;
  const locked = gpt.locked;
  const external = gpt.external_url;
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Click interceptor — when this card sits inside the 6 Months Mentorship
  // tab and the user hasn't completed onboarding, EVERY click (whether the
  // GPT is locked or granted) routes through the appropriate gate modal
  // instead of silently no-op'ing or redirecting.
  const interceptClick = (e) => {
    if (external) return;                       // Open external link normally
    if (!requiresOnboarding) {
      if (locked) {
        // Locked outside onboarding (rare for non-admins) — surface a hint.
        e.preventDefault();
      }
      return;
    }
    // requiresOnboarding === true → block and route to the right gate.
    e.preventDefault();
    if (!tncAccepted) {
      onTncRequired?.();
    } else {
      onOrientationRequired?.(gpt);
    }
  };

  // CTA target — external GPTs (e.g. Noteology) open in a new tab; in-app
  // GPTs route to the chat page. For onboarding-gated cards we keep a stub
  // href (#) since the click handler always preventDefaults.
  const ctaProps = external
    ? {
        as: "a",
        href: external,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {
        as: Link,
        to: requiresOnboarding ? "#" : `/chat/${gpt.id}`,
        onClick: interceptClick,
      };

  const CtaTag = ctaProps.as;
  const ctaRest = { ...ctaProps };
  delete ctaRest.as;

  const themedImage =
    theme === "dark"
      ? gpt.image_light_url || gpt.image_dark_url
      : gpt.image_dark_url || gpt.image_light_url;
  const hasImage = Boolean(themedImage);

  // Image-led card variant — used when a GPT ships with custom artwork.
  if (hasImage) {
    return (
      <article
        className={`agent-card-img fade-up ${locked ? "is-locked" : ""}`}
        style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
        data-testid={`gpt-gallery-card-${gpt.id}`}
      >
        <div className="agent-card-img-media">
          <img src={themedImage} alt={gpt.name} loading="lazy" />
        </div>

        <div className="agent-card-img-body">
          <h3 className="agent-card-img-title">{gpt.name}</h3>
          <p className="agent-card-img-desc">{gpt.description}</p>

          <div className="agent-card-img-chips">
            <span className="agent-chip">
              <Icon size={12} weight="bold" /> {gpt.category}
            </span>
            <span className="agent-chip agent-chip-accent">
              {locked ? (
                <>
                  <Lock size={12} weight="bold" /> Locked
                </>
              ) : (
                <>
                  <Sparkle size={12} weight="fill" /> Free
                </>
              )}
            </span>
          </div>

          {locked && !requiresOnboarding ? (
            <span
              className="agent-cta-btn is-locked"
              data-testid={`gpt-card-locked-${gpt.id}`}
            >
              <Lock size={14} weight="bold" /> Locked
            </span>
          ) : locked && requiresOnboarding ? (
            <button
              type="button"
              onClick={() => (tncAccepted ? onOrientationRequired?.(gpt) : onTncRequired?.())}
              className="agent-cta-btn is-locked"
              data-testid={`gpt-card-locked-${gpt.id}`}
            >
              <Lock size={14} weight="bold" /> Locked — see why
            </button>
          ) : (
            <CtaTag
              {...ctaRest}
              className="agent-cta-btn"
              data-testid={`gpt-card-start-${gpt.id}`}
            >
              {external ? "Open Noteology" : "Open chat"}
              <ArrowUpRight size={14} weight="bold" />
            </CtaTag>
          )}
        </div>
      </article>
    );
  }

  // Fallback: original bento icon-led card for GPTs without custom artwork
  return (
    <article
      className={`agent-card fade-up ${locked ? "is-locked" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 50}ms` }}
      data-testid={`gpt-gallery-card-${gpt.id}`}
    >
      <div className="agent-card-head">
        <div className="agent-card-icon">
          {gpt.avatar_url ? (
            <img src={gpt.avatar_url} alt="" />
          ) : (
            <Icon size={26} weight="duotone" />
          )}
        </div>
        {locked ? (
          <span className="agent-card-status locked">
            <Lock size={10} weight="bold" /> Locked
          </span>
        ) : (
          <span className="agent-card-status">Free</span>
        )}
      </div>

      <div className="agent-card-body">
        <div className="agent-card-cat">{gpt.category}</div>
        <h3 className="agent-card-title">{gpt.name}</h3>
        <p className="agent-card-desc">{gpt.description}</p>
      </div>

      <div className="agent-card-foot">
        {gpt.conversation_starter ? (
          <span className="agent-card-engine">→ {gpt.conversation_starter}</span>
        ) : (
          <span className="agent-card-engine">{gpt.category}</span>
        )}
        {locked && !requiresOnboarding ? (
          <span className="agent-open-btn is-locked" data-testid={`gpt-card-locked-${gpt.id}`}>
            <Lock size={12} weight="bold" /> Locked
            <span className="agent-open-btn-arrow"><Lock size={12} weight="bold" /></span>
          </span>
        ) : locked && requiresOnboarding ? (
          <button
            type="button"
            onClick={() => (tncAccepted ? onOrientationRequired?.(gpt) : onTncRequired?.())}
            className="agent-open-btn is-locked"
            data-testid={`gpt-card-locked-${gpt.id}`}
          >
            <Lock size={12} weight="bold" /> Locked — see why
            <span className="agent-open-btn-arrow"><Lock size={12} weight="bold" /></span>
          </button>
        ) : (
          <CtaTag
            {...ctaRest}
            className="agent-open-btn"
            data-testid={`gpt-card-start-${gpt.id}`}
          >
            {external ? "Open" : "Open"}
            <span className="agent-open-btn-arrow"><ArrowUpRight size={14} weight="bold" /></span>
          </CtaTag>
        )}
      </div>
    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Centered T&C modal — auto-opens the first time a non-admin user lands on
   the 6 Months Mentorship tab. Only "I Agree" proceeds; closing the modal
   keeps them on the tab but they cannot open any locked GPT until they agree. */
function TermsModal({ onClose, onAccepted }) {
  const [tncText, setTncText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/tnc");
        if (!cancelled) {
          setTncText(data.text || "");
        }
      } catch (e) {
        if (!cancelled) {
          setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load terms");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleAgree = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/onboarding/tnc/accept");
      onAccepted?.(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="tnc-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tnc-modal-title"
      data-testid="mentorship-tnc-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="tnc-modal-card" style={{ position: "relative" }}>
        <button
          type="button"
          className="tnc-modal-close"
          onClick={onClose}
          aria-label="Close terms modal"
          data-testid="mentorship-tnc-close"
        >
          <X size={16} weight="bold" />
        </button>

        <header className="tnc-modal-head">
          <div className="tnc-modal-badge">
            <GraduationCap size={26} weight="duotone" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="tnc-modal-eyebrow">6 Months Mentorship · Step 1 of 2</p>
            <h2 className="tnc-modal-title" id="tnc-modal-title">
              Welcome to the <em>mentorship program</em>
            </h2>
            <p className="tnc-modal-sub">
              Before you open a mentorship GPT, read these short terms. You only need to do this once.
            </p>
          </div>
        </header>

        <div className="tnc-modal-body">
          {loading ? (
            <div className="py-12 flex justify-center"><div className="spinner" /></div>
          ) : (
            <article className="tnc-scroll markdown-body" data-testid="mentorship-tnc-text">
              <ReactMarkdown>{tncText}</ReactMarkdown>
            </article>
          )}
        </div>

        <footer className="tnc-modal-foot">
          {error && (
            <p className="tnc-modal-error" data-testid="mentorship-tnc-error">{error}</p>
          )}
          <span className="tnc-modal-meta">
            By clicking &ldquo;I Agree&rdquo;, you accept the terms above.
          </span>
          <button
            type="button"
            className="tnc-modal-agree"
            onClick={handleAgree}
            disabled={loading || submitting}
            data-testid="mentorship-tnc-agree"
          >
            {submitting ? (
              <>Recording…</>
            ) : (
              <>
                <CheckCircle size={18} weight="bold" /> I Agree &amp; Continue
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Orientation-lock modal — explains why a mentorship GPT can't be opened yet
   and CTAs the user straight into the orientation form. Shown for ALL
   clicks on mentorship cards (locked OR granted) while the user has T&C
   accepted but hasn't submitted the orientation form. */
function OrientationLockModal({ gpt, onClose, onContinue }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="tnc-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orient-lock-title"
      data-testid="orientation-lock-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="tnc-modal-card" style={{ position: "relative", maxWidth: 540 }}>
        <button
          type="button"
          className="tnc-modal-close"
          onClick={onClose}
          aria-label="Close lock modal"
          data-testid="orientation-lock-close"
        >
          <X size={16} weight="bold" />
        </button>

        <header className="tnc-modal-head">
          <div className="tnc-modal-badge" style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.22), rgba(245,158,11,0.08))", color: "#b45309" }}>
            <Lock size={26} weight="duotone" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p className="tnc-modal-eyebrow">6 Months Mentorship · Orientation required</p>
            <h2 className="tnc-modal-title" id="orient-lock-title">
              <em>{gpt?.name || "This GPT"}</em> is locked
            </h2>
            <p className="tnc-modal-sub">
              All mentorship GPTs unlock once you finish the short orientation form. Your coach reads
              every answer before your first session — so they&rsquo;re ready, and so is this GPT.
            </p>
          </div>
        </header>

        <footer className="tnc-modal-foot" style={{ borderTop: "1px solid var(--soft-line)" }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            data-testid="orientation-lock-cancel"
          >
            Not now
          </button>
          <button
            type="button"
            className="tnc-modal-agree"
            onClick={onContinue}
            data-testid="orientation-lock-continue"
          >
            <ArrowRight size={18} weight="bold" /> Fill orientation form
          </button>
        </footer>
      </div>
    </div>
  );
}
